import { ReferralRequest } from '../services/referrals.service';

// Mock referral requests for demonstration
export const generateMockReferralRequests = (employeeId: string): ReferralRequest[] => {
  const mockRequests: ReferralRequest[] = [
    {
      id: 'ref_req_1',
      candidateId: 'user_john_doe',
      candidateName: 'John Doe',
      candidateEmail: 'john.doe@example.com',
      candidateTitle: 'Senior Frontend Engineer',
      candidateExperience: '5 years',
      candidateSkills: ['React', 'TypeScript', 'Node.js', 'GraphQL'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'I am very interested in joining your team. I have 5 years of experience in React and TypeScript, and have led multiple successful projects.',
      status: 'pending',
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
    },
    {
      id: 'ref_req_2',
      candidateId: 'user_jane_smith',
      candidateName: 'Jane Smith',
      candidateEmail: 'jane.smith@example.com',
      candidateTitle: 'Senior Backend Engineer',
      candidateExperience: '7 years',
      candidateSkills: ['Python', 'Django', 'PostgreSQL', 'AWS', 'Kubernetes'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=jane',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'Looking forward to contributing to your innovative projects. I specialize in scalable backend systems and have experience with high-traffic applications.',
      status: 'pending',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'ref_req_3',
      candidateId: 'user_mike_johnson',
      candidateName: 'Mike Johnson',
      candidateEmail: 'mike.johnson@example.com',
      candidateTitle: 'DevOps Engineer',
      candidateExperience: '4 years',
      candidateSkills: ['Docker', 'Kubernetes', 'Terraform', 'Jenkins', 'AWS'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=mike',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'Excited about the opportunity to work on Apple infrastructure. I have strong experience with cloud-native technologies.',
      status: 'pending',
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'ref_req_4',
      candidateId: 'user_sarah_wilson',
      candidateName: 'Sarah Wilson',
      candidateEmail: 'sarah.wilson@example.com',
      candidateTitle: 'Senior SRE Engineer',
      candidateExperience: '6 years',
      candidateSkills: ['Go', 'Prometheus', 'Grafana', 'Kubernetes', 'Istio'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=sarah',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'As an experienced SRE, I am passionate about building reliable systems at scale. Would love to discuss how I can contribute to Apple.',
      status: 'accepted',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'ref_req_5',
      candidateId: 'user_alex_chen',
      candidateName: 'Alex Chen',
      candidateEmail: 'alex.chen@example.com',
      candidateTitle: 'Junior Developer',
      candidateExperience: '1 year',
      candidateSkills: ['JavaScript', 'React', 'HTML', 'CSS'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=alex',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'Recent bootcamp graduate eager to learn and grow at Apple.',
      status: 'declined',
      declineReason: 'Looking for more senior candidates at this time',
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'ref_req_6',
      candidateId: 'user_emma_davis',
      candidateName: 'Emma Davis',
      candidateEmail: 'emma.davis@example.com',
      candidateTitle: 'Full Stack Developer',
      candidateExperience: '3 years',
      candidateSkills: ['Vue.js', 'Node.js', 'MongoDB', 'Docker'],
      candidateAvatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=emma',
      targetCompanyId: 'apple',
      targetCompanyName: 'Apple',
      targetEmployeeId: employeeId,
      message: 'Currently in discussion about the role. Would like to know more about the team structure.',
      status: 'chat',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      updatedAt: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
      hasNewMessage: true,
      lastMessageTime: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 hours ago
    }
  ];

  return mockRequests;
};

// Initialize mock data on first load
export const initializeMockReferralRequests = (employeeId: string) => {
  const existingData = localStorage.getItem(`referralRequests_${employeeId}`);
  if (!existingData) {
    const mockData = generateMockReferralRequests(employeeId);
    localStorage.setItem(`referralRequests_${employeeId}`, JSON.stringify(mockData));
    
    // Initialize candidate ratings
    const candidateRatings = {
      'user_john_doe': {
        candidateId: 'user_john_doe',
        referralSuccess: 75,
        totalApplications: 8,
        declineCount: 2,
        qualityScore: 85
      },
      'user_jane_smith': {
        candidateId: 'user_jane_smith',
        referralSuccess: 90,
        totalApplications: 10,
        declineCount: 1,
        qualityScore: 92
      },
      'user_mike_johnson': {
        candidateId: 'user_mike_johnson',
        referralSuccess: 60,
        totalApplications: 5,
        declineCount: 2,
        qualityScore: 65
      },
      'user_sarah_wilson': {
        candidateId: 'user_sarah_wilson',
        referralSuccess: 80,
        totalApplications: 12,
        declineCount: 2,
        qualityScore: 88
      },
      'user_alex_chen': {
        candidateId: 'user_alex_chen',
        referralSuccess: 20,
        totalApplications: 15,
        declineCount: 12,
        qualityScore: 35
      },
      'user_emma_davis': {
        candidateId: 'user_emma_davis',
        referralSuccess: 70,
        totalApplications: 6,
        declineCount: 2,
        qualityScore: 75
      }
    };
    
    // Store ratings in localStorage
    Object.entries(candidateRatings).forEach(([candidateId, rating]) => {
      localStorage.setItem(`candidateRating_${candidateId}`, JSON.stringify(rating));
    });
  }
};